import { computed, reactive, readonly } from 'vue';
import { apiOrigin } from './config';

type AuthStatus = 'authenticated' | 'unauthenticated';

type AuthState = {
  status: AuthStatus;
  accessToken?: string;
  error?: string;
  isSigningIn: boolean;
  /// Set when a guest link attempt resolved to a different, pre-existing
  /// account. The UI confirms abandoning the guest trial before the token is
  /// applied via confirmAccountSwitch().
  pendingAccountSwitch?: { accessToken: string };
};

type BackendTokens = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
};

type LinkOutcome = 'linked' | 'existing_account';

type LinkResponse = BackendTokens & {
  outcome: LinkOutcome;
};

const accessTokenStorageKey = 'alfrec.web.access-token';
const guestSecretStorageKey = 'alfrec.web.guest-secret';
const guestUserIdStorageKey = 'alfrec.web.guest-user-id';
let expirationTimer: number | undefined;

const jwtPayload = (token: string): Record<string, unknown> | undefined => {
  const payload = token.split('.')[1];
  if (!payload) {
    return undefined;
  }

  try {
    const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return undefined;
  }
};

const jwtExpiresAt = (token: string): number | undefined => {
  const payload = jwtPayload(token);
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : undefined;
};

const jwtSubject = (token: string): string | undefined => {
  const payload = jwtPayload(token);
  const subject = payload?.sub;
  if (typeof subject !== 'string') {
    return undefined;
  }
  const trimmed = subject.trim();
  return trimmed.length === 0 ? undefined : trimmed;
};

const isUsableAccessToken = (token: string | null): token is string => {
  if (!token) {
    return false;
  }
  const expiresAt = jwtExpiresAt(token);
  return expiresAt !== undefined && expiresAt > Date.now() + 30_000;
};

// MARK: - Guest credential store (web analog of the iOS GuestAccountStore)

const readStorageValue = (key: string): string | undefined => {
  try {
    return window.localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const storedGuestSecret = (): string | undefined => {
  const secret = readStorageValue(guestSecretStorageKey);
  return secret && secret.length >= 32 ? secret : undefined;
};

const storedGuestUserId = (): string | undefined =>
  readStorageValue(guestUserIdStorageKey) || undefined;

const storeGuestCredential = (secret: string, userId: string) => {
  try {
    window.localStorage.setItem(guestSecretStorageKey, secret);
    window.localStorage.setItem(guestUserIdStorageKey, userId);
  } catch {
    // The current tab can still use the in-memory guest session.
  }
};

const clearGuestCredential = () => {
  try {
    window.localStorage.removeItem(guestSecretStorageKey);
    window.localStorage.removeItem(guestUserIdStorageKey);
  } catch {
    // Nothing else to clear.
  }
};

const generateGuestSecret = (): string => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');
};

const isGuestToken = (token: string): boolean => {
  const guestUserId = storedGuestUserId();
  return guestUserId !== undefined && jwtSubject(token) === guestUserId;
};

// MARK: - Session state

/// Reads the persisted access token. Returns the token when usable, and
/// whether an expired token belonged to the guest account (which allows a
/// silent guest re-authentication instead of showing the sign-in gate).
const readStoredAccessToken = (): {
  token?: string;
  expiredGuestSession: boolean;
} => {
  try {
    const token = window.localStorage.getItem(accessTokenStorageKey);
    if (isUsableAccessToken(token)) {
      return { token, expiredGuestSession: false };
    }
    window.localStorage.removeItem(accessTokenStorageKey);
    if (token && isGuestToken(token) && storedGuestSecret()) {
      return { expiredGuestSession: true };
    }
  } catch {
    // Storage can be unavailable in hardened/private browser contexts.
  }
  return { expiredGuestSession: false };
};

const initialTokenState = readStoredAccessToken();
const initialAccessToken = initialTokenState.token;
const mutableSession = reactive<AuthState>({
  status: initialAccessToken ? 'authenticated' : 'unauthenticated',
  accessToken: initialAccessToken,
  isSigningIn: false,
});

const scheduleAccessTokenExpiration = (token: string) => {
  if (expirationTimer !== undefined) {
    window.clearTimeout(expirationTimer);
  }
  const expiresAt = jwtExpiresAt(token);
  if (expiresAt === undefined) {
    return;
  }
  expirationTimer = window.setTimeout(
    () => {
      if (mutableSession.accessToken !== token) {
        return;
      }
      if (isGuestToken(token)) {
        // Guest sessions renew silently from the device-held secret instead
        // of bouncing the user to the sign-in gate.
        void resumeGuestSession();
        return;
      }
      clearSession('Your session expired. Sign in again to continue.');
    },
    Math.max(0, expiresAt - Date.now()),
  );
};

const setAccessToken = (token: string) => {
  mutableSession.accessToken = token;
  mutableSession.status = 'authenticated';
  mutableSession.error = undefined;
  try {
    window.localStorage.setItem(accessTokenStorageKey, token);
  } catch {
    // The current tab can still use the in-memory session.
  }
  scheduleAccessTokenExpiration(token);
};

export const clearSession = (error?: string) => {
  if (expirationTimer !== undefined) {
    window.clearTimeout(expirationTimer);
    expirationTimer = undefined;
  }
  mutableSession.status = 'unauthenticated';
  mutableSession.accessToken = undefined;
  mutableSession.error = error;
  mutableSession.isSigningIn = false;
  mutableSession.pendingAccountSwitch = undefined;
  try {
    window.localStorage.removeItem(accessTokenStorageKey);
  } catch {
    // Nothing else to clear.
  }
};

/// Sign out, deliberately retaining the guest credential: "Use without an
/// account" re-authenticates the same guest account and its data (iOS parity).
export const logout = () => {
  clearSession();
};

const readErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  const payload = (await response.json().catch(() => undefined)) as
    | { message?: string; error?: string }
    | undefined;
  return payload?.message ?? payload?.error ?? fallback;
};

// MARK: - Sign-in flows

export const signInWithGoogleCredential = async (idToken: string) => {
  if (isGuestSession.value) {
    // OAuth from an authorized guest session links the identity onto the
    // current account instead of signing into a new one (iOS parity).
    await linkGoogleCredential(idToken);
    return;
  }

  if (mutableSession.isSigningIn) {
    return;
  }
  mutableSession.isSigningIn = true;
  mutableSession.error = undefined;

  try {
    const response = await fetch(`${apiOrigin}/mobile/oauth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider: 'google', id_token: idToken }),
    });

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, `Sign-in failed (${response.status}).`),
      );
    }

    const tokens = (await response.json()) as Partial<BackendTokens>;
    if (
      typeof tokens.access_token !== 'string' ||
      !isUsableAccessToken(tokens.access_token)
    ) {
      throw new Error('The server returned an invalid access token.');
    }

    // The first web version deliberately retains only the 24-hour access token.
    // Persisting a long-lived refresh token in browser storage would increase
    // the impact of an XSS bug.
    setAccessToken(tokens.access_token);
  } catch (error) {
    clearSession(error instanceof Error ? error.message : 'Google sign-in failed.');
    throw error;
  } finally {
    mutableSession.isSigningIn = false;
  }
};

/// Mints a guest account from the device-held secret (or re-authenticates the
/// one previously minted with it) and authorizes the session with ordinary
/// tokens.
export const signInAnonymously = async () => {
  if (mutableSession.isSigningIn) {
    return;
  }
  mutableSession.isSigningIn = true;
  mutableSession.error = undefined;

  const secret = storedGuestSecret() ?? generateGuestSecret();
  try {
    const response = await fetch(`${apiOrigin}/mobile/oauth/anonymous`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_secret: secret }),
    });

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          `Could not start without an account (${response.status}).`,
        ),
      );
    }

    const tokens = (await response.json()) as Partial<BackendTokens>;
    if (
      typeof tokens.access_token !== 'string' ||
      !isUsableAccessToken(tokens.access_token)
    ) {
      throw new Error('The server returned an invalid access token.');
    }
    const userId = jwtSubject(tokens.access_token);
    if (!userId) {
      throw new Error('The guest token is missing its account id.');
    }

    storeGuestCredential(secret, userId);
    setAccessToken(tokens.access_token);
  } catch (error) {
    clearSession(
      error instanceof Error
        ? error.message
        : 'Could not start without an account.',
    );
    throw error;
  } finally {
    mutableSession.isSigningIn = false;
  }
};

/// Silently re-authenticates the retained guest account after its 24-hour
/// access token lapsed. Never runs after an explicit logout (logout removes
/// the persisted access token, so the lapsed-guest boot path cannot match).
const resumeGuestSession = async () => {
  if (mutableSession.isSigningIn) {
    return;
  }
  try {
    await signInAnonymously();
  } catch {
    // signInAnonymously already published the error on the session.
  }
};

/// Links a Google identity onto the current guest session. On "linked" the
/// user id is unchanged and the session continues seamlessly; on
/// "existing_account" the returned token is held for user confirmation.
/// A failed or cancelled link leaves the guest session intact.
const linkGoogleCredential = async (idToken: string) => {
  if (mutableSession.isSigningIn) {
    return;
  }
  const bearerToken = mutableSession.accessToken;
  if (!isUsableAccessToken(bearerToken ?? null)) {
    mutableSession.error = 'Your guest session expired while linking.';
    return;
  }
  mutableSession.isSigningIn = true;
  mutableSession.error = undefined;

  try {
    const response = await fetch(`${apiOrigin}/mobile/oauth/link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
      },
      body: JSON.stringify({ provider: 'google', id_token: idToken }),
    });

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          `Could not create your account (${response.status}).`,
        ),
      );
    }

    const payload = (await response.json()) as Partial<LinkResponse>;
    if (
      typeof payload.access_token !== 'string' ||
      !isUsableAccessToken(payload.access_token)
    ) {
      throw new Error('The server returned an invalid access token.');
    }

    if (payload.outcome === 'linked') {
      // Same user id: the local database and all trial progress carry over.
      // Clear the credential first so isGuestSession recomputes to false when
      // the token mutation triggers reactivity.
      clearGuestCredential();
      setAccessToken(payload.access_token);
    } else if (payload.outcome === 'existing_account') {
      mutableSession.pendingAccountSwitch = {
        accessToken: payload.access_token,
      };
    } else {
      throw new Error('The server returned an unknown link outcome.');
    }
  } catch (error) {
    // The guest session stays authorized; only surface the failure.
    mutableSession.error =
      error instanceof Error ? error.message : 'Could not create your account.';
    throw error;
  } finally {
    mutableSession.isSigningIn = false;
  }
};

/// The user confirmed switching to the pre-existing account a link attempt
/// resolved to. The guest trial credential is discarded and the page reloads
/// so local storage rebinds to the other account (origin-wide account
/// transition).
export const confirmAccountSwitch = () => {
  const pending = mutableSession.pendingAccountSwitch;
  if (!pending) {
    return;
  }
  mutableSession.pendingAccountSwitch = undefined;
  clearGuestCredential();
  setAccessToken(pending.accessToken);
  window.location.reload();
};

export const cancelAccountSwitch = () => {
  mutableSession.pendingAccountSwitch = undefined;
};

// MARK: - Accessors

export const currentAccessToken = (): string | undefined => {
  const token = mutableSession.accessToken;
  if (isUsableAccessToken(token ?? null)) {
    return token;
  }
  clearSession('Your session expired. Sign in again to continue.');
  return undefined;
};

export const currentUserId = (): string | undefined => {
  const token = currentAccessToken();
  if (!token) {
    return undefined;
  }
  return jwtSubject(token);
};

export const hasGuestCredential = (): boolean =>
  storedGuestSecret() !== undefined;

export const authSession = readonly(mutableSession);
export const isAuthenticated = computed(
  () =>
    mutableSession.status === 'authenticated' &&
    mutableSession.accessToken !== undefined,
);

/// True while the authorized session belongs to a guest (anonymous) account
/// minted from the device-held credential.
export const isGuestSession = computed(() => {
  if (
    mutableSession.status !== 'authenticated' ||
    mutableSession.accessToken === undefined
  ) {
    return false;
  }
  return isGuestToken(mutableSession.accessToken);
});

if (initialAccessToken) {
  scheduleAccessTokenExpiration(initialAccessToken);
} else if (initialTokenState.expiredGuestSession) {
  void resumeGuestSession();
}

window.addEventListener('storage', (event) => {
  if (event.key !== accessTokenStorageKey) {
    return;
  }
  if (isUsableAccessToken(event.newValue)) {
    mutableSession.accessToken = event.newValue;
    mutableSession.status = 'authenticated';
    mutableSession.error = undefined;
    scheduleAccessTokenExpiration(event.newValue);
  } else {
    clearSession();
  }
});
